import React, { useState, useEffect } from 'react';
import { X, Sparkles, Smartphone, Download, Share, Plus } from 'lucide-react';
import usePWA from '../hooks/usePWA';

const PWAInstallPrompt = () => {
  const { isPWA, isInstallable, installApp } = usePWA();
  const [isVisible, setIsVisible] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  // Detect iOS browser
  const isIOS = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  };

  // Detect Safari explicitly on iOS (since chrome on iOS cannot install PWAs)
  const isSafari = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    return isIOS() && ua.includes('Safari') && !ua.includes('CriOS') && !ua.includes('FxiOS');
  };

  useEffect(() => {
    // 1. Never show if already running in standalone PWA mode
    if (isPWA) return;

    // 2. Check dismissal cooldown (e.g. 7 days)
    const checkDismissal = () => {
      try {
        const dismissedAt = localStorage.getItem('pwa_prompt_dismissed_at');
        if (dismissedAt) {
          const sevenDays = 7 * 24 * 60 * 60 * 1000;
          if (Date.now() - parseInt(dismissedAt, 10) < sevenDays) {
            return true; // Still in cooldown
          }
        }
      } catch (e) {
        console.warn('LocalStorage error:', e);
      }
      return false;
    };

    if (checkDismissal()) return;

    // 3. Show prompt after a small delay (3 seconds) to allow the app to settle
    const timer = setTimeout(() => {
      // Show for Android/Desktop (where isInstallable is true)
      // Or show for iOS Safari (where installation is manual)
      if (isInstallable || (isSafari() && !isPWA)) {
        setIsVisible(true);
      }
    }, 4000);

    return () => clearTimeout(timer);
  }, [isPWA, isInstallable]);

  const handleDismiss = () => {
    try {
      localStorage.setItem('pwa_prompt_dismissed_at', String(Date.now()));
    } catch (e) {}
    setIsVisible(false);
  };

  const handleInstallClick = async () => {
    if (isInstallable) {
      // Android/Chrome native install flow
      const success = await installApp();
      if (success) {
        setIsVisible(false);
      }
    } else if (isSafari()) {
      // iOS Safari manual instruction flow
      setShowIOSInstructions(true);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Installation Banner */}
      <div 
        className="fixed bottom-24 left-4 right-4 md:left-auto md:right-6 md:w-96 z-[8990] bg-white/95 backdrop-blur-xl border border-gray-200/80 rounded-2xl shadow-2xl p-4 transition-all duration-500 transform translate-y-0 animate-in slide-in-from-bottom-10 fade-in duration-300"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom) + 12px)' }}
      >
        <div className="flex items-start gap-3">
          {/* App Icon */}
          <div className="w-12 h-12 bg-gradient-to-br from-[#FF6600] to-[#E55A00] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
            <span className="text-white font-extrabold text-lg tracking-wider">TV</span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <h4 className="text-sm font-extrabold text-gray-900 leading-tight">Install TestingVala</h4>
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-black uppercase rounded-md tracking-wider">
                <Sparkles className="w-3 h-3 text-[#FF6600]" /> App
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
              Add to your home screen for instant access, offline support, and push notifications.
            </p>
          </div>

          <button 
            onClick={handleDismiss}
            className="p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Dismiss install prompt"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-2.5 mt-4 pt-1">
          <button 
            onClick={handleDismiss}
            className="px-3.5 py-1.5 text-xs font-bold text-gray-500 hover:text-gray-700 transition-colors"
          >
            Maybe Later
          </button>
          <button 
            onClick={handleInstallClick}
            className="bg-[#FF6600] hover:bg-[#E55A00] text-white px-4 py-2 rounded-xl font-bold text-xs shadow-md shadow-orange-100 hover:shadow-lg active:scale-95 transition-all flex items-center gap-1.5"
          >
            {isInstallable ? <Download className="w-3.5 h-3.5" /> : <Smartphone className="w-3.5 h-3.5" />}
            <span>Install Now</span>
          </button>
        </div>
      </div>

      {/* iOS Instructions Overlay */}
      {showIOSInstructions && (
        <div className="fixed inset-0 z-[99999] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div 
            className="bg-white w-full max-w-sm rounded-t-3xl sm:rounded-2xl shadow-2xl p-6 overflow-hidden animate-in slide-in-from-bottom-10 duration-300 border border-gray-100"
            style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom) + 16px)' }}
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-black text-gray-900">Install on your iOS Device</h3>
              <button 
                onClick={() => setShowIOSInstructions(false)}
                className="p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-500 leading-relaxed">
                Safari on iOS does not support one-click installation, but you can add it manually in 3 simple steps:
              </p>

              <div className="space-y-3.5">
                {/* Step 1 */}
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-blue-50 border border-blue-100 text-[#0057B7] text-xs font-bold flex items-center justify-center flex-shrink-0">
                    1
                  </div>
                  <p className="text-sm text-gray-700 leading-normal mt-0.5">
                    Tap the <strong>Share</strong> button at the bottom of Safari:
                    <span className="inline-flex items-center justify-center p-1.5 bg-gray-100 rounded-lg ml-1.5 text-gray-600">
                      <Share className="w-4 h-4" />
                    </span>
                  </p>
                </div>

                {/* Step 2 */}
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-blue-50 border border-blue-100 text-[#0057B7] text-xs font-bold flex items-center justify-center flex-shrink-0">
                    2
                  </div>
                  <p className="text-sm text-gray-700 leading-normal mt-0.5">
                    Scroll down the options list and select <strong>Add to Home Screen</strong>:
                    <span className="inline-flex items-center justify-center p-1.5 bg-gray-100 rounded-lg ml-1.5 text-gray-600">
                      <Plus className="w-4 h-4" />
                    </span>
                  </p>
                </div>

                {/* Step 3 */}
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-blue-50 border border-blue-100 text-[#0057B7] text-xs font-bold flex items-center justify-center flex-shrink-0">
                    3
                  </div>
                  <p className="text-sm text-gray-700 leading-normal mt-0.5">
                    Tap <strong>Add</strong> in the top right corner of the screen to complete the installation.
                  </p>
                </div>
              </div>
            </div>

            <button 
              onClick={() => setShowIOSInstructions(false)}
              className="w-full bg-[#0057B7] hover:bg-[#004494] text-white py-3 rounded-xl font-bold text-sm shadow-md hover:shadow-lg active:scale-95 transition-all mt-6"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;
